import { FC, createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { matchRoutePath } from '../../libs/router/router-helpers';
import { useDebounce } from '../../scripts/hooks/useDebounce';
import { withSiteMeta } from '../../HOCS/withSiteMeta';
import { stripPathLocale } from '../../../common/localeShared';
import { commonRoutes } from '../../../gameConfig/commonRoutes';
import { useSiteConfig } from '../../scripts/selectors/global';
import { canUseDOM } from 'exenv';

// context to share about whether we should show instream video (there is a dockable region)
type TInStreamVideoContext = {
  isInStream: null | boolean;
  wasInStream: null | boolean;
  inStreamRefCount: number;
  setIsInStream: (val: boolean) => void;
  adjustInStreamRefCount: (val: number) => void;
};

export const InStreamVideoContext = createContext<TInStreamVideoContext>({
  isInStream: null,
  wasInStream: null,
  inStreamRefCount: 0,
  setIsInStream: (val: boolean) => {
    /* nop */
  },
  adjustInStreamRefCount: (val: number) => {
    /* nop */
  }
});

const FORCED_INSTREAM_ROUTES = [
  '/',
  commonRoutes.CharacterDetails,
  commonRoutes.GuildDetails,
  '/mythic-plus-runs/:season/:id', // MythicKeystoneRun
  commonRoutes.MythicKeystoneLoggedRun
];

export function isForcedInStreamPath(currentPath: string) {
  let isOnForcedInStreamPath = false;

  for (const route of FORCED_INSTREAM_ROUTES) {
    if (matchRoutePath(route, currentPath)) {
      isOnForcedInStreamPath = true;
      break;
    }
  }

  return isOnForcedInStreamPath;
}

const InStreamVideoEligibilityProviderBase: FC<any> = (props) => {
  if (!canUseDOM) {
    // we don't want SSR to try to do anything with this
    return props.children;
  }

  if (props.currentFeatures.no_ads) {
    // can return early because currentFeatures won't change after initial load
    return props.children;
  }

  const siteConfig = useSiteConfig();
  if (siteConfig.isEmbedded) {
    // can return early because siteConfig won't change after initial load
    return props.children;
  }

  const [isInStream, setIsInStream] = useState<null | boolean>(null);
  const [wasInStream, setWasInStream] = useState<null | boolean>(null);
  const [inStreamRefCount, setInStreamRefCount] = useState<number>(0);
  const location = useLocation();
  const debouncedLocation = useDebounce(location, 500) as any;

  /*
  Logic:
  - if new location supports instream then no need to debounce
  - otherwise debounce in case the component just needs to be added
  */

  // allow certain paths to just force instream on, because we know that it's supported there
  useEffect(() => {
    const isCurrentPageForcedInStream = isForcedInStreamPath(stripPathLocale(location.pathname));
    const isDebouncedPageForcedInStream = debouncedLocation
      ? isForcedInStreamPath(stripPathLocale(debouncedLocation.pathname))
      : false;

    const useInStream = inStreamRefCount > 0 || isCurrentPageForcedInStream || isDebouncedPageForcedInStream;

    if (useInStream !== isInStream) {
      console.info(
        `[VIDEO] Status changing to ${
          useInStream ? 'INSTREAM' : 'OUTSTREAM'
        }. isCurrentInStream=${isCurrentPageForcedInStream} isDebouncedInStream=${isDebouncedPageForcedInStream} inStreamRefCount=${inStreamRefCount}`
      );
      setIsInStream(useInStream);
    }

    if (wasInStream !== useInStream) {
      setWasInStream(useInStream);
    }
  }, [location, debouncedLocation, inStreamRefCount]);

  return (
    <InStreamVideoContext.Provider
      value={{
        inStreamRefCount,
        isInStream,
        wasInStream,
        setIsInStream: (val) => {
          if (val !== isInStream) {
            console.info(`[VIDEO] Eligibility Provider changed instream status to: ${val ? 'INSTREAM' : 'OUTSTREAM'}`);
          }
          return setIsInStream(val);
        },
        adjustInStreamRefCount: (val) => {
          return setInStreamRefCount((previousValue: number) => {
            const refCount = previousValue + val;
            // console.info(
            //   `[VIDEO] Eligibility Provider changed instream refcount to ${refCount} (${previousValue} + ${val})`
            // );
            return refCount;
          });
        }
      }}
    >
      {props.children}
    </InStreamVideoContext.Provider>
  );
};

export const InStreamVideoEligibilityProvider = withSiteMeta(InStreamVideoEligibilityProviderBase);

/**
 * InStreamStateToggler
 */
export function InStreamStateToggler(props: any) {
  const { isInStream } = useContext(InStreamVideoContext);
  return isInStream ? props.inStreamComponent : props.outStreamComponent;
}
